import { useState, useEffect, useMemo } from 'react';
import { useStateSelector } from '../store/selectors';
import { useDispatch } from 'react-redux';
import { FetchEngagementConferences } from '../slices/marketplace/meetings/thunks/fetch-engagement-conferences.thunk';
import { useCurrentMarketplaceParty } from './useCurrentMarketplaceParty';
import { MeetingParticipantsType } from '../slices/marketplace/meetings/models/enums/meeting-participant-type.enum';
import { isBuyerPartOfEngagement } from '../utils/isBuyerPartOfEngagement';

export const useHasMeetingsOrClips = (engagementId: number, participantTypes?: MeetingParticipantsType[]): boolean => {
    const [hasClips, setHasClips] = useState(false);
    const axios = useStateSelector((state) => state.core.axios);
    const dispatch = useDispatch() as any;
    const engagementMeetingsState = useStateSelector(
        (state: any) => state.engagementMeetings
    );
    const selectedOpportunity = useStateSelector(
        (state: any) => state.opportunities?.selectedOpportuity
    );
    const party = useCurrentMarketplaceParty(engagementId);
    
    const hasMeetings = useMemo(() => {
        if (!engagementMeetingsState?.engagementMeetings) {
            return false;
        }
        const meetingsForEngagement = engagementMeetingsState.engagementMeetings.filter(
            (meeting: any) =>
                meeting.engagementId === engagementId &&
                (!participantTypes || participantTypes.includes(meeting.participantsType))
        );
        return meetingsForEngagement.length > 0;
    }, [engagementMeetingsState?.engagementMeetings, engagementId, participantTypes]);
    
    const isPartOfEngagement = useMemo(
        () => isBuyerPartOfEngagement(party, selectedOpportunity, engagementId),
        [party, selectedOpportunity, engagementId]
    );
    
    // Reset hasClips when engagementId changes
    useEffect(() => {
        setHasClips(false);
    }, [engagementId]);
    
    useEffect(() => {
        if (!isPartOfEngagement) {
            return;
        }

        // Always fetch meetings when engagementId changes to ensure we have the latest data
        if (engagementId) {
            dispatch(FetchEngagementConferences(engagementId));
        }
    }, [engagementId, dispatch, isPartOfEngagement]);

    useEffect(() => {
        if (!isPartOfEngagement) {
            setHasClips(false);
            return;
        }
        
        const checkClips = async () => {
            if (!engagementId) {
                return;
            }

            try {
                const myClipsResponse = await axios.get(
                    `/api/Marketplace/Engagements/${engagementId}/EngagementMeetings/clips`
                );
                
                if (myClipsResponse.status === 200 && myClipsResponse.data?.length > 0) {
                    setHasClips(true);
                    return;
                }
                
                const sharedClipsResponse = await axios.get(
                    `/api/Marketplace/Engagements/${engagementId}/EngagementMeetings/clips/shared`
                );
                
                if (sharedClipsResponse.status === 200 && sharedClipsResponse.data?.length > 0) {
                    setHasClips(true);
                } else {
                    setHasClips(false);
                }
            } catch (error) {
                setHasClips(false);
            }
        };
        
        checkClips();
    }, [engagementId, axios, isPartOfEngagement]);

    if (!isPartOfEngagement) {
        return false;
    }

    if (participantTypes) {
        return hasMeetings;
    }

    return hasMeetings || hasClips;
};